/* ==========================================================================
   dns-locator.js — retailer locator: map, filters, radius search.

   No runtime API calls. Everything it needs is either inlined by the section
   (metaobject path) or in two theme assets (fallback path + ZIP3 centroids).
   Search never leaves the browser, so there is nothing to rate-limit and
   nothing to pay for.

   Three record kinds:
     storefront  a shop with a street address -> a blue map pin
     mobile      no premises; located by the show venues it travels to
     online      no premises at all; listed in a quiet strip, never a pin
   ========================================================================== */

(function () {
  'use strict';

  var EARTH_MI = 3958.8;
  var ZIP3_URL_ATTR = 'data-zip3-src';
  var PAGE = 25;              // cards rendered per batch (pins are never paged)

  function toRad(d) { return (d * Math.PI) / 180; }

  function distanceMi(aLat, aLng, bLat, bLng) {
    var dLat = toRad(bLat - aLat);
    var dLng = toRad(bLng - aLng);
    var h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * EARTH_MI * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function addressLine(r) {
    var bits = [r.address1, [r.city, r.state].filter(Boolean).join(', '), r.zip];
    return bits.filter(Boolean).join(' · ');
  }

  function directionsUrl(r) {
    // Built from the address text, never from the coordinates: 13 records are
    // pinned at their ZIP centroid because a suite number defeated the geocoder,
    // and the printed address is the one that is actually correct.
    var q = [r.name, r.address1, r.city, r.state, r.zip].filter(Boolean).join(', ');
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
  }

  function telHref(p) { return 'tel:' + String(p).replace(/[^0-9+]/g, ''); }

  /* ---------------------------------------------------------------------- */

  function Locator(root) {
    this.root = root;
    this.map = null;
    this.clusters = null;
    this.markers = {};
    this.origin = null;      // {lat, lng, label} once the user searches or shares location
    this.zip3 = null;
    this.query = '';
    this.radius = 50;
    this.active = { storefront: true, shows: true, online: false, iea: false };
    this.ieaEnabled = root.getAttribute('data-iea-enabled') === 'true';
    this.data = { venues: [], stockists: [] };
    this.byVenue = {};       // venue handle -> [stockist]

    this.el = {
      body: root.querySelector('[data-loc-body]'),
      map: root.querySelector('[data-loc-map]'),
      results: root.querySelector('[data-loc-results]'),
      count: root.querySelector('[data-loc-count]'),
      search: root.querySelector('[data-loc-search]'),
      radius: root.querySelector('[data-loc-radius]'),
      near: root.querySelector('[data-loc-near]'),
      reset: root.querySelector('[data-loc-reset]'),
      online: root.querySelector('[data-loc-online]'),
      iea: root.querySelector('[data-loc-iea]'),
      onlineGrid: root.querySelector('[data-loc-online-grid]'),
      chips: Array.prototype.slice.call(root.querySelectorAll('[data-loc-filter]')),
      tabs: Array.prototype.slice.call(root.querySelectorAll('[data-loc-view]'))
    };

    // Written here rather than in the Liquid so a no-script browser never sees a
    // permanent "Loading..." sitting above the <noscript> list.
    this.el.results.innerHTML = '<p class="dns-loc__empty">Loading retailers&hellip;</p>';
    this.load();
  }

  Locator.prototype.load = function () {
    var self = this;
    var inline = this.root.querySelector('[data-loc-data]');
    var raw = inline ? inline.textContent.trim() : '';

    if (raw) {
      try {
        this.boot(JSON.parse(raw));
        return;
      } catch (e) {
        // Malformed inline JSON must not take the page down — fall through to
        // the asset, which is generated rather than templated.
        if (window.console) console.warn('[dns-locator] inline data unreadable, using asset', e);
      }
    }

    var src = this.root.getAttribute('data-fallback-src');
    if (!src) { this.fail('No retailer data is available.'); return; }

    fetch(src, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (d) { self.boot(d); })
      .catch(function (e) {
        if (window.console) console.error('[dns-locator]', e);
        self.fail('We could not load the retailer list. Please refresh.');
      });
  };

  Locator.prototype.fail = function (msg) {
    this.el.results.innerHTML = '<p class="dns-loc__empty">' + esc(msg) + '</p>';
    this.el.count.textContent = '';
  };

  Locator.prototype.boot = function (data) {
    var self = this;
    this.data.venues = (data.venues || []).filter(function (v) { return v.lat && v.lng; });
    this.data.stockists = data.stockists || [];

    // Only the stockist -> venue edge is stored. Invert it once here so a venue
    // pin can answer "who is here" without a lookup per render.
    this.data.venues.forEach(function (v) { self.byVenue[v.handle] = []; });
    this.data.stockists.forEach(function (s) {
      (s.shows || []).forEach(function (h) {
        if (self.byVenue[h]) self.byVenue[h].push(s);
      });
    });

    this.root.classList.add('is-ready');
    // Read the chips' starting state out of the markup rather than trusting the
    // constructor's defaults — otherwise a merchant flipping a chip's default in
    // the Liquid silently disagrees with what actually renders.
    this.el.chips.forEach(function (chip) {
      self.active[chip.getAttribute('data-loc-filter')] = chip.getAttribute('aria-pressed') === 'true';
    });
    this.stats();
    this.bind();
    this.initMap();
    this.render();
  };

  Locator.prototype.stats = function () {
    var shops = this.data.stockists.filter(function (s) { return s.kind === 'storefront'; });
    var states = {};
    shops.forEach(function (s) { if (s.state) states[s.state] = 1; });
    var set = function (k, v) {
      var el = this.root.querySelector('[data-loc-stat="' + k + '"]');
      if (el) el.textContent = v;
    }.bind(this);
    set('shops', shops.length);
    set('states', Object.keys(states).length);
    set('shows', this.data.venues.length);

    var counts = {
      storefront: shops.length,
      shows: this.data.venues.length,
      online: this.data.stockists.filter(function (s) { return s.kind === 'online'; }).length,
      iea: this.data.stockists.filter(function (s) { return s.iea; }).length
    };
    this.el.chips.forEach(function (chip) {
      var n = this.root.querySelector('[data-loc-n="' + chip.getAttribute('data-loc-filter') + '"]');
      if (n) n.textContent = counts[chip.getAttribute('data-loc-filter')] || 0;
    }, this);

    // Nothing to toggle if the store has no online retailers yet.
    if (!counts.online) {
      var c = this.root.querySelector('[data-loc-filter="online"]');
      if (c) c.hidden = true;
    }
    // Same for the IEA chip: no flagged partners (or the promo switched off) = no chip.
    if (!counts.iea || !this.ieaEnabled) {
      var ic = this.root.querySelector('[data-loc-filter="iea"]');
      if (ic) ic.hidden = true;
      this.active.iea = false;
    }
  };

  Locator.prototype.bind = function () {
    var self = this;
    var t;

    this.el.search.addEventListener('input', function () {
      clearTimeout(t);
      t = setTimeout(function () { self.onSearch(self.el.search.value); }, 220);
    });

    this.el.search.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); clearTimeout(t); self.onSearch(self.el.search.value); }
    });

    this.el.radius.addEventListener('change', function () {
      self.radius = parseInt(self.el.radius.value, 10) || 0;
      self.render();
    });

    this.el.near.addEventListener('click', function () { self.locate(); });

    this.el.reset.addEventListener('click', function () {
      self.origin = null;
      self.query = '';
      self.el.search.value = '';
      self.el.reset.hidden = true;
      self.render();
      self.fitAll();
    });

    this.el.chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var key = chip.getAttribute('data-loc-filter');
        var next = chip.getAttribute('aria-pressed') !== 'true';
        // Never let the user switch everything off and stare at a blank page.
        // `iea` narrows rather than adds, so it does not count as "something is on".
        var others = Object.keys(self.active).filter(function (k) { return k !== key && k !== 'iea'; });
        if (!next && !others.some(function (k) { return self.active[k]; })) return;
        self.active[key] = next;
        chip.setAttribute('aria-pressed', String(next));
        self.render();
      });
    });

    this.el.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var view = tab.getAttribute('data-loc-view');
        self.el.body.setAttribute('data-view', view);
        self.el.tabs.forEach(function (o) {
          o.setAttribute('aria-selected', String(o === tab));
        });
        // The map is display:none in list view, so Leaflet sized it at 0x0 and
        // markercluster clustered against meaningless bounds — tiles came back on
        // invalidateSize() but every marker stayed invisible. Re-adding the layers
        // after the resize is what actually makes pins appear.
        if (view === 'map' && self.map) {
          setTimeout(function () {
            self.map.invalidateSize();
            self.paintMap(self.rows || []);
            if (!self.origin) self.fitAll();
          }, 80);
        }
      });
    });
  };

  /* ---- search: ZIP lookup is local, everything else is a text match ------ */

  Locator.prototype.onSearch = function (value) {
    var self = this;
    var v = String(value || '').trim();
    this.el.reset.hidden = !v;

    var zip = v.match(/^\s*(\d{3,5})\s*$/);
    if (zip) {
      this.zipOrigin(zip[1]).then(function (ok) {
        if (!ok) { self.query = v.toLowerCase(); self.origin = null; }
        self.render();
        if (self.origin) self.map.setView([self.origin.lat, self.origin.lng], self.radius > 100 ? 6 : 8);
      });
      return;
    }

    this.query = v.toLowerCase();
    this.origin = null;
    this.render();
  };

  Locator.prototype.fetchJson = function (url, cacheKey) {
    var self = this;
    this._cache = this._cache || {};
    if (this._cache[cacheKey]) return Promise.resolve(this._cache[cacheKey]);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { if (j) self._cache[cacheKey] = j; return j; })
      .catch(function () { return null; });
  };

  Locator.prototype.zipOrigin = function (zip) {
    var self = this;

    // Exact ZIP5 first — the ZIP3 centroid is out by 10 miles in a city and 30+
    // in the rural west, which is enough to put the wrong shop at the top of the
    // list. The ZIP5 table is sharded by first digit so this pulls ~24 KB, and
    // only for visitors who actually type a ZIP.
    var shards = this.zip5Map;
    if (shards === undefined) {
      try { shards = this.zip5Map = JSON.parse(this.root.getAttribute('data-zip5-map') || '{}'); }
      catch (e) { shards = this.zip5Map = {}; }
    }

    var five = zip.length === 5 && shards[zip[0]]
      ? this.fetchJson(shards[zip[0]], 'z5' + zip[0])
      : Promise.resolve(null);

    return five.then(function (table) {
      if (table && table[zip]) return { ll: table[zip], exact: true };
      return self.fetchJson(self.root.getAttribute(ZIP3_URL_ATTR), 'z3').then(function (t3) {
        var p = zip.slice(0, 3);
        return t3 && t3[p] ? { ll: t3[p], exact: false } : null;
      });
    }).then(function (hit) {
      if (!hit) return false;
      self.query = '';
      self.origin = {
        lat: hit.ll[0],
        lng: hit.ll[1],
        label: 'ZIP ' + zip + (hit.exact ? '' : ' (approx)')
      };
      return true;
    });
  };

  Locator.prototype.locate = function () {
    var self = this;
    if (!navigator.geolocation) return;
    this.el.near.disabled = true;
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        self.el.near.disabled = false;
        self.query = '';
        self.el.search.value = '';
        self.el.reset.hidden = false;
        self.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'your location' };
        self.render();
        self.map.setView([self.origin.lat, self.origin.lng], 8);
      },
      function () {
        self.el.near.disabled = false;
        self.el.count.textContent = 'We could not get your location. Try a ZIP code instead.';
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  };

  /* ---- map -------------------------------------------------------------- */

  Locator.prototype.initMap = function () {
    if (typeof window.L === 'undefined') return;
    var L = window.L;

    this.map = L.map(this.el.map, { scrollWheelZoom: false, zoomControl: true });
    // Drop Leaflet's default "Leaflet" prefix link; the tile provider's credit stays.
    this.map.attributionControl.setPrefix(false);
    L.tileLayer(this.root.getAttribute('data-tile-url'), {
      attribution: this.root.getAttribute('data-tile-attribution'),
      maxZoom: parseInt(this.root.getAttribute('data-max-zoom'), 10) || 17
    }).addTo(this.map);

    // Scroll-wheel zoom off by default so the page still scrolls past the map;
    // a click enables it, which is the behaviour people expect from a locator.
    this.map.on('click focus', function () { this.scrollWheelZoom.enable(); });
    this.map.on('mouseout', function () { this.scrollWheelZoom.disable(); });

    var self = this;
    this.clusters = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 48,
      iconCreateFunction: function (cluster) {
        var kids = cluster.getAllChildMarkers();
        var allShows = kids.every(function (m) { return m.options.dnsKind === 'venue'; });
        return L.divIcon({
          html: '<div class="dns-loc-cluster' + (allShows ? ' dns-loc-cluster--show' : '') + '">' +
                cluster.getChildCount() + '</div>',
          className: '',
          iconSize: [38, 38]
        });
      }
    });
    this.map.addLayer(this.clusters);
    this.fitAll();

    // The map is created inside a sticky/tabbed container, so its size can be
    // wrong on first paint. One deferred invalidate covers both cases.
    setTimeout(function () { self.map.invalidateSize(); }, 120);
  };

  Locator.prototype.fitAll = function () {
    if (!this.map) return;
    // Continental US. Better than fitting bounds to the data, which would let a
    // single outlier decide the whole framing.
    this.map.fitBounds([[24.5, -125.0], [49.4, -66.9]], { padding: [12, 12] });
  };

  Locator.prototype.pin = function (rec, kind) {
    var L = window.L;
    var isVenue = kind === 'venue';
    var marker = L.marker([rec.lat, rec.lng], {
      dnsKind: kind,
      dnsHandle: rec.handle,
      title: rec.name,
      icon: L.divIcon({
        html: '<div class="dns-loc-pin' + (isVenue ? ' dns-loc-pin--show' : '') + '"></div>',
        className: '',
        iconSize: [26, 26],
        iconAnchor: [13, 24],
        popupAnchor: [0, -22]
      })
    });

    var html = '<b>' + esc(rec.name) + '</b><br>';
    if (isVenue) {
      var who = this.byVenue[rec.handle] || [];
      if (rec.season) html += esc(rec.season) + '<br>';
      html += esc(addressLine(rec)) + '<br>';
      html += '<br><b>' + who.length + ' stockist' + (who.length === 1 ? '' : 's') + ' here:</b><br>' +
              who.map(function (s) { return esc(s.name); }).join('<br>');
    } else {
      html += esc(addressLine(rec));
      if (rec.phone) html += '<br><a href="' + telHref(rec.phone) + '">' + esc(rec.phone) + '</a>';
      html += '<br><a href="' + directionsUrl(rec) + '" target="_blank" rel="noopener">Directions</a>';
    }
    marker.bindPopup(html);

    var self = this;
    marker.on('click', function () { self.highlight(rec.handle); });
    return marker;
  };

  Locator.prototype.highlight = function (handle) {
    // Clicking a pin can target a card that has not been paged in yet — reveal
    // enough batches to reach it before trying to scroll to it.
    var idx = (this.rows || []).findIndex(function (r) { return r.rec.handle === handle; });
    if (idx >= this.shown) {
      this.shown = Math.ceil((idx + 1) / PAGE) * PAGE;
      this.paint();
    }

    var cards = this.el.results.querySelectorAll('.dns-loc__card');
    Array.prototype.forEach.call(cards, function (c) {
      c.classList.toggle('is-active', c.getAttribute('data-handle') === handle);
    });
    var hit = this.el.results.querySelector('.dns-loc__card.is-active');
    if (hit && hit.scrollIntoView) hit.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  Locator.prototype.focus = function (handle) {
    var m = this.markers[handle];
    if (!m || !this.map) return;
    if (window.matchMedia('(max-width: 980px)').matches) {
      var mapTab = this.el.tabs.filter(function (t) { return t.getAttribute('data-loc-view') === 'map'; })[0];
      if (mapTab) mapTab.click();
    }
    this.clusters.zoomToShowLayer(m, function () { m.openPopup(); });
  };

  /* ---- filter + render --------------------------------------------------- */

  Locator.prototype.matches = function (rec) {
    if (!this.query) return true;
    return [rec.name, rec.city, rec.state, rec.zip, rec.address1]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .indexOf(this.query) !== -1;
  };

  Locator.prototype.withDistance = function (list) {
    var self = this;
    var out = list.map(function (r) {
      var c = Object.create(r);
      c.__d = self.origin ? distanceMi(self.origin.lat, self.origin.lng, r.lat, r.lng) : null;
      return c;
    });
    if (self.origin) {
      if (self.radius) out = out.filter(function (r) { return r.__d <= self.radius; });
      out.sort(function (a, b) { return a.__d - b.__d; });
    } else {
      out.sort(function (a, b) { return a.name.localeCompare(b.name); });
    }
    return out;
  };

  Locator.prototype.render = function () {
    var self = this;
    var rows = [];

    if (this.active.storefront) {
      rows = rows.concat(
        this.withDistance(
          this.data.stockists.filter(function (s) {
            if (self.active.iea && !s.iea) return false;   // IEA chip narrows to partners
            return s.kind === 'storefront' && s.lat && self.matches(s);
          })
        ).map(function (r) { return { kind: 'shop', rec: r }; })
      );
    }

    if (this.active.shows) {
      rows = rows.concat(
        this.withDistance(
          this.data.venues.filter(function (v) {
            // In IEA mode a venue only earns a row if an IEA partner trades there —
            // that is how the mobile-store partners (no premises) stay findable.
            if (self.active.iea && !(self.byVenue[v.handle] || []).some(function (s) { return s.iea; })) return false;
            if (self.matches(v)) return true;
            // A venue also matches if one of the shops travelling there does —
            // "where can I find Quail Hollow" should surface their show stops.
            return (self.byVenue[v.handle] || []).some(function (s) { return self.matches(s); });
          })
        ).map(function (r) { return { kind: 'venue', rec: r }; })
      );
    }

    if (this.origin) {
      rows.sort(function (a, b) { return a.rec.__d - b.rec.__d; });
    }

    this.rows = rows;
    this.shown = PAGE;
    this.paint();
    this.paintOnline();
    this.paintIea();
    this.paintMap(rows);
  };

  Locator.prototype.paintIea = function () {
    if (!this.el.iea) return;
    this.el.iea.hidden = !(this.active.iea && this.ieaEnabled);
  };

  Locator.prototype.paint = function () {
    var self = this;
    var rows = this.rows || [];

    if (!rows.length) {
      this.el.count.textContent = '';
      this.el.results.innerHTML =
        '<div class="dns-loc__empty"><b>Nothing here yet.</b><br>' +
        'Try a wider distance, or clear the search to see every stockist.</div>';
      return;
    }

    this.el.count.textContent = rows.length + (rows.length === 1 ? ' result' : ' results') +
      (this.origin ? ' near ' + this.origin.label : '');

    // Render a page at a time. Every pin is always on the map; it is only the
    // cards that page. Dumping all 234 at once built a 35,000px column on a
    // phone, where the list container cannot scroll independently.
    var slice = rows.slice(0, this.shown);
    var html = slice.map(function (row) {
      return row.kind === 'venue' ? self.venueCard(row.rec) : self.shopCard(row.rec);
    }).join('');

    if (rows.length > this.shown) {
      html += '<button class="dns-loc__btn dns-loc__btn--ghost dns-loc__more" type="button" data-loc-more>' +
        'Show ' + Math.min(PAGE, rows.length - this.shown) + ' more of ' + rows.length + '</button>';
    }
    this.el.results.innerHTML = html;

    var more = this.el.results.querySelector('[data-loc-more]');
    if (more) {
      more.addEventListener('click', function () {
        self.shown += PAGE;
        self.paint();
      });
    }

    Array.prototype.forEach.call(this.el.results.querySelectorAll('.dns-loc__card'), function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('a')) return;
        self.focus(card.getAttribute('data-handle'));
        self.highlight(card.getAttribute('data-handle'));
      });
    });
  };

  Locator.prototype.shopCard = function (r) {
    var travels = (r.shows || []).length;
    var badges = [];
    if (this.ieaEnabled && r.iea) badges.push('<span class="dns-loc__badge dns-loc__badge--iea">IEA partner</span>');
    if (travels) badges.push('<span class="dns-loc__badge dns-loc__badge--show">Also at ' + travels + ' show' + (travels === 1 ? '' : 's') + '</span>');
    return '' +
      '<article class="dns-loc__card' + (this.ieaEnabled && r.iea ? ' dns-loc__card--iea' : '') + '" data-handle="' + esc(r.handle) + '" tabindex="0">' +
        '<h3 class="dns-loc__name">' + esc(r.name) + '</h3>' +
        '<p class="dns-loc__addr">' + esc(addressLine(r)) + '</p>' +
        '<div class="dns-loc__meta">' +
          (r.phone ? '<a href="' + telHref(r.phone) + '">' + esc(r.phone) + '</a>' : '') +
          '<a href="' + directionsUrl(r) + '" target="_blank" rel="noopener">Directions</a>' +
          (r.website ? '<a href="' + esc(r.website) + '" target="_blank" rel="noopener">Website</a>' : '') +
          (r.__d != null ? '<span class="dns-loc__dist">' + r.__d.toFixed(1) + ' mi</span>' : '') +
        '</div>' +
        (badges.length ? '<div class="dns-loc__badges">' + badges.join('') + '</div>' : '') +
      '</article>';
  };

  Locator.prototype.venueCard = function (v) {
    var self = this;
    var who = this.byVenue[v.handle] || [];
    var ieaHere = this.ieaEnabled && who.some(function (s) { return s.iea; });
    return '' +
      '<article class="dns-loc__card dns-loc__card--show" data-handle="' + esc(v.handle) + '" tabindex="0">' +
        '<h3 class="dns-loc__name">' + esc(v.name) + '</h3>' +
        '<p class="dns-loc__addr">' + esc(addressLine(v)) + '</p>' +
        '<div class="dns-loc__meta">' +
          '<a href="' + directionsUrl(v) + '" target="_blank" rel="noopener">Directions</a>' +
          (v.__d != null ? '<span class="dns-loc__dist">' + v.__d.toFixed(1) + ' mi</span>' : '') +
        '</div>' +
        '<div class="dns-loc__badges">' +
          '<span class="dns-loc__badge dns-loc__badge--show">Show venue</span>' +
          (v.season ? '<span class="dns-loc__badge dns-loc__badge--season">' + esc(v.season) + '</span>' : '') +
          (ieaHere ? '<span class="dns-loc__badge dns-loc__badge--iea">IEA partner here</span>' : '') +
        '</div>' +
        (who.length
          ? '<div class="dns-loc__shows"><p class="dns-loc__shows-h">Find these stockists here</p>' +
            who.map(function (s) { return esc(s.name) + (self.ieaEnabled && s.iea ? ' <span class="dns-loc__iea-tag">IEA</span>' : ''); }).join('<br>') + '</div>'
          : '') +
      '</article>';
  };

  Locator.prototype.paintOnline = function () {
    var online = this.data.stockists.filter(function (s) { return s.kind === 'online'; });
    if (!this.active.online || !online.length) { this.el.online.hidden = true; return; }
    this.el.online.hidden = false;
    this.el.onlineGrid.innerHTML = online.map(function (s) {
      return '<div class="dns-loc__online-card"><b>' + esc(s.name) + '</b>' +
        (s.website
          ? '<a href="' + esc(s.website) + '" target="_blank" rel="noopener nofollow">Shop</a>'
          : '<span class="dns-loc__dist">Online</span>') +
        '</div>';
    }).join('');
  };

  Locator.prototype.paintMap = function (rows) {
    if (!this.map || !this.clusters) return;
    var self = this;
    this.clusters.clearLayers();
    this.markers = {};

    var layers = [];
    rows.forEach(function (row) {
      var m = self.pin(row.rec, row.kind === 'venue' ? 'venue' : 'shop');
      self.markers[row.rec.handle] = m;
      layers.push(m);
    });
    this.clusters.addLayers(layers);

    if (this.origin) {
      if (this.originMarker) this.map.removeLayer(this.originMarker);
      this.originMarker = window.L.circleMarker([this.origin.lat, this.origin.lng], {
        radius: 7, color: '#13355b', weight: 3, fillColor: '#a3edf0', fillOpacity: 1
      }).addTo(this.map);
    } else if (this.originMarker) {
      this.map.removeLayer(this.originMarker);
      this.originMarker = null;
    }
  };

  /* ---------------------------------------------------------------------- */

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('.dns-loc'), function (root) {
      if (root.dataset.locBooted) return;
      root.dataset.locBooted = '1';
      new Locator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', init);
})();
